import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Link, Text } from '@hopskipdrive/react-components';
import { startCase } from 'lodash';

import { analyticsDeleteRiderContact, analyticsEditRiderContact } from '../../../api/services/Analytics';
import { ApiResponse } from '../../../api/utils/responseTransform';
import { useFlags } from '../../../contexts/LDContext';
import { useToastStore } from '../../../shared/stores/toastStore';
import { formatPhone } from '../../../utils/helpers/formatPhone';

import { DeleteModal } from './DeleteModal';
import { EditModal } from './EditModal';
import { ViewModal } from './ViewModal';

interface IContactProps {
  contact: RidersContact;
  riderFullName?: string;
  riderSlug: string;
  contacts: RidersContact[];
  updateContacts: (data) => Promise<ApiResponse<{
    message?: string | undefined;
  }> | null>;
}

export const Contact: FC<IContactProps> = ({ contact, riderFullName, riderSlug, contacts, updateContacts }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { iepStructuredNotesEnabled } = useFlags();
  const useNewForm = iepStructuredNotesEnabled;
  const navigate = useNavigate();
  const { resetToasts } = useToastStore();
  const [modalOpen, setModalOpen] = useState<'edit' | 'delete' | 'view' | false>(false);

  const handleUpdateContact = async (data) => {
    setIsSubmitting(true);
    const updatedContacts = contacts.map((c) =>
      c.id === contact.id
        ? {
            ...data,
            id: contact.id,
            email: data.role === 'caregiver' ? data.email : '',
            relationship: data.relationship || '',
          }
        : c
    );
    const response = await updateContacts({ passengersContactsAttributes: updatedContacts });

    if (response?.type === 'success') {
      setModalOpen(false);
      analyticsEditRiderContact(data);
    }
    setIsSubmitting(false);
  };

  const handleDeleteContact = async (contactDetails: RidersContact) => {
    setIsSubmitting(true);
    const response = await updateContacts({ passengersContactsIdsToDelete: [contactDetails.id] });

    if (response?.type === 'success') {
      setModalOpen(false);
      analyticsDeleteRiderContact(contact);
    }
    setIsSubmitting(false);
  };

  const handleNameClick = () => {
    if (useNewForm) {
      navigate(`/riders/${riderSlug}/edit?section=contacts`);
    } else {
      setModalOpen('view');
    }
  };

  useEffect(() => {
    !modalOpen && resetToasts();
  }, [modalOpen]);

  const roleDisplay = startCase(contact.role.replace('_', ' '));
  const relationshipDisplay = contact.relationship ? startCase(contact.relationship) : '';
  const roleAndRelationship = relationshipDisplay ? `${roleDisplay} / ${relationshipDisplay}` : roleDisplay;

  return (
    <>
      {useNewForm ? (
        <Box display="flex" gap="12px" pb={3} data-testid="rider-contact">
          <Box display="flex" flexDirection="column" width="215px">
            <Link fontWeight={500} data-testid="contact-name" color="primary" onClick={handleNameClick}>
              {contact.fullName}
            </Link>
            <Text fontWeight={300} textColor="secondaryText">
              {roleAndRelationship}
            </Text>
          </Box>
          <Box display="flex" flexDirection="column" flex={1} minWidth={0}>
            <Text fontWeight={300} textColor="secondaryText">
              {formatPhone(contact.phoneNumber)}
            </Text>
            {contact.email && (
              <Text fontWeight={300} textColor="secondaryText" sx={{ wordBreak: 'break-all' }}>
                {contact.email}
              </Text>
            )}
          </Box>
        </Box>
      ) : (
        <Box display="flex" justifyContent="space-between" pb={3} data-testid="rider-contact">
          <Box display="flex" flexDirection="column">
            <Link fontWeight={500} data-testid="contact-name" color="primary" onClick={handleNameClick}>
              {contact.fullName}
            </Link>
            <Text fontWeight={300} textColor="secondaryText">
              {roleDisplay}
            </Text>
          </Box>
          <Text fontWeight={300} textColor="secondaryText">
            {formatPhone(contact.phoneNumber)}
          </Text>
        </Box>
      )}
      <EditModal
        isOpen={modalOpen === 'edit'}
        onCancel={() => setModalOpen(false)}
        onSubmit={handleUpdateContact}
        contact={contact}
        handleDeleteButton={() => setModalOpen('delete')}
        isSubmitting={isSubmitting}
      />
      <ViewModal
        isOpen={modalOpen === 'view'}
        onCancel={() => setModalOpen(false)}
        openEditModal={() => setModalOpen('edit')}
        contact={contact}
        handleDeleteButton={() => setModalOpen('delete')}
      />
      <DeleteModal
        isOpen={modalOpen === 'delete'}
        contact={contact}
        onCancel={() => setModalOpen(false)}
        handleDelete={() => handleDeleteContact(contact)}
        riderFullName={riderFullName}
        isSubmitting={isSubmitting}
      />
    </>
  );
};
